// Mobile-optimized survey page with camera flow
// Large touch-friendly controls for easy photo capture on iPhone

import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Camera,
  Image,
  Check,
  ChevronRight,
  Trash2,
  X,
  Loader2,
  MessageSquare,
  AlertCircle,
  Edit3,
  Building2,
  MapPin,
  User,
  Mic,
  FileText,
  Monitor,
  Sparkles
} from 'lucide-react'
import useSurveyStore from '../../stores/surveyStore'
import useCategoryStore from '../../stores/categoryStore'
import { CameraCapture } from '../survey'
import { Modal, ConfirmModal } from '../common'
import { SURVEY_STATUS_LABELS, SURVEY_STATUS_COLORS } from '../../utils/constants'

// Get image URL - matches PhotoCard logic
function getImageUrl(photo) {
  return photo.thumbnailUrl || photo.imageUrl || photo.thumbnail || photo.imageData || ''
}

export default function MobileSurveyPage({ surveyId }) {
  // CRITICAL: Log immediately on component mount
  console.log('[MobileSurveyPage] Component mounting with surveyId:', surveyId)

  const navigate = useNavigate()

  const [selectedCategory, setSelectedCategory] = useState(null)
  const [showCamera, setShowCamera] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)


  // Photo notes editing state
  const [editingPhoto, setEditingPhoto] = useState(null)
  const [editingNotes, setEditingNotes] = useState('')

  // Survey details editing state
  const [showEditDetails, setShowEditDetails] = useState(false)
  const [editFormData, setEditFormData] = useState({})
  const [savingDetails, setSavingDetails] = useState(false)

  // Category notes editing state
  const [editingCategoryCode, setEditingCategoryCode] = useState(null)
  const [categoryNotes, setCategoryNotes] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [savingCategoryNotes, setSavingCategoryNotes] = useState(false)

  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const loading = useSurveyStore(state => state.loading)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)
  const deleteSurvey = useSurveyStore(state => state.deleteSurvey)
  const addPhoto = useSurveyStore(state => state.addPhoto)
  const deletePhoto = useSurveyStore(state => state.deletePhoto)
  const clearCurrentSurvey = useSurveyStore(state => state.clearCurrentSurvey)
  const togglePhotoAnalyse = useSurveyStore(state => state.togglePhotoAnalyse)
  const updatePhotoNotes = useSurveyStore(state => state.updatePhotoNotes)
  const updateSurvey = useSurveyStore(state => state.updateSurvey)

  const categories = useCategoryStore(state => state.getActiveCategories())
  const customCategories = useCategoryStore(state => state.customCategories)
  const loadCategories = useCategoryStore(state => state.loadCategories)

  useEffect(() => {
    console.log('[MobileSurveyPage] useEffect - surveyId:', surveyId)
    if (surveyId) {
      loadSurvey(surveyId).catch(error => {
        console.error('[MobileSurveyPage] Failed to load survey:', error)
      })
      loadCategories().catch(error => {
        console.error('[MobileSurveyPage] Failed to load categories:', error)
      })
    }
    return () => clearCurrentSurvey()
  }, [surveyId, loadSurvey, loadCategories, clearCurrentSurvey])

  const handleCapture = useCallback(async (photoData) => {
    try {
      console.log('[MobileSurveyPage] handleCapture called', { selectedCategory: selectedCategory?.code, photoDataSize: photoData?.imageData?.length })

      if (!selectedCategory) {
        console.error('[MobileSurveyPage] No selected category')
        return
      }

      const newPhoto = await addPhoto({
        ...photoData,
        categoryCode: selectedCategory.code
      })

      console.log('[MobileSurveyPage] Photo added successfully, showing notes modal', newPhoto)
      setShowCamera(false)

      // Immediately show notes modal after capture for easy dictation
      // Use the newly created photo object directly
      if (newPhoto) {
        setEditingPhoto(newPhoto)
        setEditingNotes(newPhoto.notes || '')
      }

    } catch (error) {
      console.error('[MobileSurveyPage] Failed to capture photo:', error)
      alert(`Failed to save photo: ${error.message}`)
      setShowCamera(false)
    }
  }, [selectedCategory, addPhoto])

  const handleDeleteSurvey = async () => {
    await deleteSurvey(surveyId)
    navigate('/surveys')
  }

  const handleDeletePhoto = async (photoId) => {
    await deletePhoto(photoId)
  }

  // Toggle AI analysis flag on a photo
  const handleToggleAI = async (photoId, currentValue) => {
    try {
      await togglePhotoAnalyse(photoId, !currentValue)
    } catch (error) {
      console.error('Failed to toggle AI flag:', error)
    }
  }

  // Open notes editor for a photo
  const handleEditNotes = (photo) => {
    setEditingPhoto(photo)
    setEditingNotes(photo.notes || '')
  }

  // Save photo notes
  const handleSaveNotes = async () => {
    if (!editingPhoto) return
    try {
      await updatePhotoNotes(editingPhoto.id, editingNotes)
      setEditingPhoto(null)
      setEditingNotes('')
    } catch (error) {
      console.error('Failed to save notes:', error)
    }
  }

  // Open category notes editor
  const handleEditCategoryNotes = (categoryCode) => {
    const currentNotes = currentSurvey.reviewData?.[categoryCode]?.comment || ''
    setEditingCategoryCode(categoryCode)
    setCategoryNotes(currentNotes)
  }

  // Save category notes
  const handleSaveCategoryNotes = async () => {
    if (!editingCategoryCode || !currentSurvey) return
    setSavingCategoryNotes(true)
    try {
      const updatedReviewData = {
        ...(currentSurvey.reviewData || {}),
        [editingCategoryCode]: {
          ...(currentSurvey.reviewData?.[editingCategoryCode] || {}),
          comment: categoryNotes
        }
      }
      await updateSurvey(currentSurvey.id, {
        reviewData: updatedReviewData
      })
      setEditingCategoryCode(null)
      setCategoryNotes('')
    } catch (error) {
      console.error('Failed to save category notes:', error)
      alert('Failed to save notes. Please try again.')
    } finally {
      setSavingCategoryNotes(false)
    }
  }

  // Start voice dictation
  const handleStartDictation = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice dictation is not supported on this browser. Please use Safari on iOS.')
      return
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()

    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-GB'

    recognition.onstart = () => {
      setIsRecording(true)
    }

    recognition.onresult = (event) => {
      let interimTranscript = ''
      let finalTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' '
        } else {
          interimTranscript += transcript
        }
      }

      if (finalTranscript) {
        setCategoryNotes(prev => prev + finalTranscript)
      }
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      setIsRecording(false)
      if (event.error === 'no-speech') {
        alert('No speech detected. Please try again.')
      } else if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please enable microphone permissions.')
      }
    }

    recognition.onend = () => {
      setIsRecording(false)
    }

    // Store recognition instance to stop it later
    window.currentRecognition = recognition
    recognition.start()
  }

  // Stop voice dictation
  const handleStopDictation = () => {
    if (window.currentRecognition) {
      window.currentRecognition.stop()
      window.currentRecognition = null
    }
    setIsRecording(false)
  }

  // Open edit details modal
  const handleOpenEditDetails = () => {
    setEditFormData({
      propertyName: currentSurvey.propertyName || '',
      address: currentSurvey.address || '',
      postcode: currentSurvey.postcode || '',
      surveyorName: currentSurvey.surveyorName || '',
      surveyLevel: currentSurvey.surveyLevel || 1
    })
    setShowEditDetails(true)
  }

  // Save survey details
  const handleSaveDetails = async () => {
    if (!currentSurvey) return
    setSavingDetails(true)
    try {
      await updateSurvey(currentSurvey.id, editFormData)
      setShowEditDetails(false)
    } catch (error) {
      console.error('Failed to save survey details:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setSavingDetails(false)
    }
  }


  // Get photo count per category
  const getPhotosForCategory = (categoryCode) => {
    return currentPhotos.filter(p => p.categoryCode === categoryCode)
  }

  console.log('[MobileSurveyPage] Render - loading:', loading, 'currentSurvey:', !!currentSurvey, 'surveyId:', surveyId)

  if (loading || !currentSurvey) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="bg-primary-700 text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/surveys')} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold">Loading...</h1>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  const totalPhotos = currentPhotos?.length || 0
  const categoriesWithPhotos = currentPhotos ? new Set(currentPhotos.map(p => p.categoryCode)).size : 0

  console.log('[MobileSurveyPage] Rendering main UI - totalPhotos:', totalPhotos, 'categoriesWithPhotos:', categoriesWithPhotos)

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Header */}
      <div
        className="bg-primary-700 text-white px-4 py-3 flex-shrink-0"
        style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/surveys')}
            className="p-2 -ml-2 hover:bg-primary-600 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold truncate">{currentSurvey.propertyName}</h1>
            <div className="flex items-center gap-2 text-sm text-primary-200">
              <span>{totalPhotos} photos</span>
              <span>|</span>
              <span>{categoriesWithPhotos} categories</span>
            </div>
          </div>

          {/* Desktop View Toggle - Only show on tablets (iPads), not phones */}
          {window.innerWidth >= 640 && window.innerWidth < 1024 && (
            <button
              onClick={() => navigate(`/survey/${surveyId}?view=desktop`)}
              className="p-2 hover:bg-primary-600 rounded-lg flex items-center gap-2"
              title="Switch to Desktop View"
            >
              <Monitor className="w-5 h-5" />
              <span className="text-sm hidden sm:inline">Desktop</span>
            </button>
          )}

          <span className={`px-2 py-1 rounded text-xs font-medium ${SURVEY_STATUS_COLORS[currentSurvey.status]}`}>
            {SURVEY_STATUS_LABELS[currentSurvey.status]}
          </span>
        </div>
      </div>

      {/* Content - Single vertical scroll with all sections */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* Survey Details Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Survey Details</h2>
            </div>
            <div className="p-4">
              <button
                onClick={handleOpenEditDetails}
                className="w-full p-4 border border-slate-200 rounded-lg flex items-center gap-3 active:bg-slate-50"
              >
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Edit3 className="w-5 h-5 text-slate-600" />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-slate-900">Edit Details</div>
                  <div className="text-sm text-slate-500">Address, surveyor, level</div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Categories Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Categories</h2>
              <p className="text-sm text-slate-500 mt-1">
                Select a category, then tap the camera button below
              </p>
            </div>
            <div className="p-4 space-y-2">
              {categories.map((category, index) => {
                const photos = getPhotosForCategory(category.code)
                const isSelected = selectedCategory?.code === category.code
                const hasNotes = currentSurvey.reviewData?.[category.code]?.comment
                return (
                  <div key={category.code} className="space-y-2">
                    <div
                      onClick={() => setSelectedCategory(isSelected ? null : category)}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-center justify-between cursor-pointer ${
                        isSelected
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-bold font-mono bg-primary-100 text-primary-700 px-2 py-1 rounded flex-shrink-0">
                            {category.code}
                          </span>
                        </div>
                        <div className="font-semibold text-slate-900">{category.name}</div>
                        {photos.length > 0 && (
                          <div className="text-sm text-slate-500 mt-1">
                            {photos.length} photo{photos.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditCategoryNotes(category.code)
                          }}
                          className={`w-9 h-9 rounded-full flex items-center justify-center ${
                            hasNotes
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-100 text-slate-400'
                          }`}
                          title="Add notes"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        {isSelected ? (
                          <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        ) : photos.length > 0 ? (
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                            <Image className="w-4 h-4 text-emerald-600" />
                          </div>
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* All Photos Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">All Photos ({totalPhotos})</h2>
                {currentPhotos.length > 0 && (
                  <span className="text-sm text-slate-500">
                    {currentPhotos.filter(p => p.analyseWithAI !== false).length} for AI
                  </span>
                )}
              </div>
            </div>
            <div className="p-4">
              {currentPhotos.length === 0 ? (
                <div className="text-center py-8">
                  <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No photos yet. Select a category above and tap the camera button.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {currentPhotos.map((photo) => {
                    const category = categories.find(c => c.code === photo.categoryCode)
                    const analyseWithAI = photo.analyseWithAI !== false
                    return (
                      <div key={photo.id} className="relative">
                        <img
                          src={getImageUrl(photo)}
                          alt={category?.name || 'Photo'}
                          className="w-full aspect-square object-cover rounded-lg"
                        />
                        {/* Category label */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 rounded-b-lg">
                          <div className="text-white text-xs font-medium truncate">
                            {category?.name || photo.categoryCode}
                          </div>
                        </div>

                        {/* Top action buttons */}
                        <div className="absolute top-2 left-2 right-2 flex justify-between">
                          {/* Left: AI toggle + Notes */}
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleToggleAI(photo.id, analyseWithAI)}
                              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
                                analyseWithAI
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-white/90 text-slate-400'
                              }`}
                              title={analyseWithAI ? 'AI analysis ON' : 'AI analysis OFF'}
                            >
                              <Sparkles className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditNotes(photo)}
                              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
                                photo.notes
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-white/90 text-slate-400'
                              }`}
                              title={photo.notes ? 'Has notes' : 'Add notes'}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Right: Delete */}
                          <button
                            onClick={() => handleDeletePhoto(photo.id)}
                            className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Actions Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Actions</h2>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>
                  Analysis and PDF generation available on desktop/tablet only
                </p>
              </div>

              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-center gap-3 active:bg-red-100"
              >
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-semibold">Delete Survey</div>
                  <div className="text-sm text-red-400">Remove permanently</div>
                </div>
              </button>
            </div>
          </div>

          {/* Version indicator */}
          <div className="text-center py-4 text-xs text-slate-400">
            v1.3.0 - Mobile Redirect Active
          </div>

          {/* Bottom spacing for safe area */}
          <div style={{ height: 'max(80px, env(safe-area-inset-bottom))' }} />
        </div>
      </div>

      {/* Bottom Camera Button - Fixed at bottom */}
      <div
        className="flex-shrink-0 p-4 bg-white border-t border-slate-200 shadow-lg"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={() => setShowCamera(true)}
          disabled={!selectedCategory}
          className={`w-full py-4 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
            selectedCategory
              ? 'bg-primary-600 text-white active:bg-primary-700'
              : 'bg-slate-200 text-slate-400'
          }`}
        >
          <Camera className="w-6 h-6 flex-shrink-0" />
          <span className="truncate">
            {selectedCategory ? `Take Photo - ${selectedCategory.name}` : 'Select a Category First'}
          </span>
        </button>
      </div>

      {/* Camera Modal - Full Screen */}
      <Modal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        size="fullscreen"
        showCloseButton={false}
      >
        <CameraCapture
          selectedCategory={selectedCategory}
          onCapture={handleCapture}
          onCancel={() => setShowCamera(false)}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSurvey}
        title="Delete Survey"
        message={`Are you sure you want to delete "${currentSurvey.propertyName}"? This will also delete all ${totalPhotos} photos. This action cannot be undone.`}
        confirmText="Delete Survey"
        variant="danger"
      />

      {/* Notes Editing Modal */}
      <Modal
        isOpen={!!editingPhoto}
        onClose={() => {
          setEditingPhoto(null)
          if (isRecording) handleStopDictation()
        }}
        title="Photo Notes"
        size="md"
      >
        {editingPhoto && (
          <div className="space-y-4">
            <img
              src={getImageUrl(editingPhoto)}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-lg mx-auto"
            />
            <p className="text-xs text-center text-slate-500">
              Notes are included in the AI analysis prompt for additional context.
            </p>
            <div className="relative">
              <textarea
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                placeholder="Add notes about this photo (e.g., 'damp patch in corner', 'crack above window')..."
                rows={4}
                className="w-full px-4 py-3 pr-14 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
              {/* Voice dictation button */}
              <button
                onClick={isRecording ? handleStopDictation : () => handleStartDictation(setEditingNotes)}
                className={`absolute bottom-3 right-3 p-2 rounded-lg transition-colors ${
                  isRecording
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                title={isRecording ? 'Stop recording' : 'Start voice dictation'}
              >
                <Mic className="w-5 h-5" />
              </button>
            </div>
            {isRecording && (
              <p className="text-sm text-red-600 text-center animate-pulse">
                Recording... Speak now
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setEditingPhoto(null)
                  if (isRecording) handleStopDictation()
                }}
                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-slate-700 font-medium"
              >
                Skip
              </button>
              <button
                onClick={handleSaveNotes}
                className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium"
              >
                Save Notes
              </button>
            </div>
            <p className="text-xs text-slate-500 text-center">
              Voice dictation uses your device's microphone. Works best in Safari on iOS.
            </p>
          </div>
        )}
      </Modal>

      {/* Edit Details Modal */}
      <Modal
        isOpen={showEditDetails}
        onClose={() => !savingDetails && setShowEditDetails(false)}
        title="Edit Survey Details"
        size="md"
      >
        <div className="space-y-4">
          {/* Property Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Property Name
            </label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={editFormData.propertyName || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, propertyName: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Smith Residence"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Full Address
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <textarea
                value={editFormData.address || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, address: e.target.value }))}
                rows={2}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="e.g., 123 High Street, London"
              />
            </div>
          </div>

          {/* Postcode */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Postcode
            </label>
            <input
              type="text"
              value={editFormData.postcode || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 uppercase"
              placeholder="e.g., SW1A 1AA"
            />
          </div>

          {/* Surveyor Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Surveyor Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={editFormData.surveyorName || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, surveyorName: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter surveyor name"
              />
            </div>
          </div>

          {/* Survey Level */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Survey Level
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 1, label: 'Level 1', desc: 'Basic' },
                { value: 2, label: 'Level 2', desc: 'Standard' },
                { value: 3, label: 'Level 3', desc: 'Full' }
              ].map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => setEditFormData(prev => ({ ...prev, surveyLevel: level.value }))}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    editFormData.surveyLevel === level.value
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-600'
                  }`}
                >
                  <div className="font-semibold text-sm">{level.label}</div>
                  <div className="text-xs opacity-75">{level.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowEditDetails(false)}
              disabled={savingDetails}
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-slate-700 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveDetails}
              disabled={savingDetails}
              className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingDetails ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Category Notes Modal with Dictation */}
      <Modal
        isOpen={!!editingCategoryCode}
        onClose={() => !savingCategoryNotes && !isRecording && setEditingCategoryCode(null)}
        title={`Notes - ${categories.find(c => c.code === editingCategoryCode)?.name || editingCategoryCode?.toUpperCase()}`}
        size="md"
      >
        {editingCategoryCode && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>
                These notes sync to the desktop app's RICS survey section for {editingCategoryCode.toUpperCase()}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Category Notes
              </label>
              <textarea
                value={categoryNotes}
                onChange={(e) => setCategoryNotes(e.target.value)}
                placeholder="Type or dictate your observations for this category..."
                rows={8}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                disabled={isRecording}
              />
            </div>

            {/* Dictation Status */}
            {isRecording && (
              <div className="flex items-center justify-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="font-medium">Recording... Speak now</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              {/* Dictation Button */}
              {!isRecording ? (
                <button
                  onClick={handleStartDictation}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg font-medium active:bg-red-700"
                >
                  <Mic className="w-5 h-5" />
                  Start Dictation
                </button>
              ) : (
                <button
                  onClick={handleStopDictation}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-red-700 text-white rounded-lg font-medium active:bg-red-800"
                >
                  <X className="w-5 h-5" />
                  Stop Recording
                </button>
              )}

              <div className="flex-1 flex gap-2">
                <button
                  onClick={() => {
                    if (isRecording) handleStopDictation()
                    setEditingCategoryCode(null)
                    setCategoryNotes('')
                  }}
                  disabled={savingCategoryNotes || isRecording}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-slate-700 font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategoryNotes}
                  disabled={savingCategoryNotes || isRecording}
                  className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingCategoryNotes ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Notes'
                  )}
                </button>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center">
              Voice dictation uses your device's microphone. Works best in Safari on iOS.
            </p>
          </div>
        )}
      </Modal>
    </div>
  )
}
